import { queryString } from './format';
import type {
  ApiSuccess,
  CheckinCandidate,
  CheckinResult,
  DashboardData,
  DemographicsData,
  DemographicScope,
  DeveloperModeState,
  AdminEventSettings,
  PaginatedRegistrations,
  RegistrationCreateResult,
  RegistrationFormData,
  RegistrationLookup,
  RegistrationSummary,
  SessionUser,
  Team,
  TrackingContext,
  PublicLookupResponse,
  PublicEventConfig,
} from '../types';

const configuredBase = import.meta.env.VITE_API_BASE_URL?.trim() ?? '';
export const API_BASE_URL = configuredBase.replace(/\/$/, '');

type JsonRecord = Record<string, unknown>;

let csrfToken: string | undefined;
const SESSION_TOKEN_KEY = 'forteens.session-token';
let sessionToken: string | undefined;

try { sessionToken = window.localStorage.getItem(SESSION_TOKEN_KEY) || window.sessionStorage.getItem(SESSION_TOKEN_KEY) || undefined; } catch { /* Optional cross-site fallback. */ }

function record(value: unknown): JsonRecord {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as JsonRecord : {};
}

function text(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : value === null || value === undefined ? fallback : String(value);
}

function numeric(value: unknown): number | undefined {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function paymentStatus(value: unknown): RegistrationSummary['paymentStatus'] {
  const status = text(value, 'pending_payment');
  return status === 'pending' ? 'pending_payment' : status as RegistrationSummary['paymentStatus'];
}

function registrationStatus(value: unknown): RegistrationSummary['status'] {
  const status = text(value, 'pending_payment');
  return status as RegistrationSummary['status'];
}

function mapTeam(value: unknown, source?: JsonRecord): Team | null {
  const input = record(value);
  const name = text(input.name ?? source?.team_name);
  if (!name) return null;
  const count = numeric(input.registrationsCount ?? input.registration_count ?? source?.registration_count);
  return {
    id: text(input.id ?? input.teamId ?? source?.team_id, name),
    name,
    color: text(input.color ?? source?.team_color, '#4a8fe7'),
    isActive: input.isActive === undefined ? input.is_active === undefined ? true : Boolean(input.is_active) : Boolean(input.isActive),
    ...(count === undefined ? {} : { registrationsCount: count }),
    number: numeric(input.number ?? input.team_number) ?? null,
    femaleCount: numeric(input.femaleCount ?? input.female_count) ?? 0,
    maleCount: numeric(input.maleCount ?? input.male_count) ?? 0,
    unknownCount: numeric(input.unknownCount ?? input.unknown_count) ?? 0,
    ageCounts: Object.fromEntries(Object.entries(record(input.ageCounts ?? input.age_counts)).map(([key, value]) => [key, numeric(value) ?? 0])),
    lastAssignedAt: text(input.lastAssignedAt ?? input.last_assigned_at) || null,
  };
}

function mapRegistration(value: unknown, masked = false): RegistrationSummary {
  const input = record(value);
  const registrationCode = text(input.registrationCode ?? input.registration_code ?? input.code, 'INDISPONÍVEL');
  return {
    id: text(input.id, `public:${registrationCode}`),
    registrationCode,
    registrationType: text(input.registrationType ?? input.registration_type, 'participant') as RegistrationSummary['registrationType'],
    participantName: text(input.participantName ?? input.participant_name ?? input.name, 'Inscrição'),
    ...(numeric(input.participantAge ?? input.participant_age ?? input.age) === undefined ? {} : { participantAge: numeric(input.participantAge ?? input.participant_age ?? input.age) }),
    sex: (text(input.sex) || null) as RegistrationSummary['sex'],
    ...(text(input.participantPhone ?? input.participant_phone ?? input.phone) ? { participantPhone: text(input.participantPhone ?? input.participant_phone ?? input.phone) } : {}),
    ...(text(input.participantEmail ?? input.participant_email ?? input.email) ? { participantEmail: text(input.participantEmail ?? input.participant_email ?? input.email) } : {}),
    foodRestriction: text(input.foodRestriction ?? input.food_restriction) || null,
    attendsForteens: input.attendsForteens === undefined && input.attends_forteens === undefined ? null : Boolean(input.attendsForteens ?? input.attends_forteens),
    attendsCell: input.attendsCell === undefined && input.attends_cell === undefined ? null : Boolean(input.attendsCell ?? input.attends_cell),
    cellName: text(input.cellName ?? input.cell_name) || null,
    responsibleName: text(input.responsibleName ?? input.responsible_name) || null,
    responsiblePhone: text(input.responsiblePhone ?? input.responsible_phone) || null,
    status: registrationStatus(input.status ?? input.registrationStatus ?? input.registration_status),
    paymentStatus: paymentStatus(input.paymentStatus ?? input.payment_status),
    paymentMethod: (text(input.paymentMethod ?? input.payment_capture_method) || null) as RegistrationSummary['paymentMethod'],
    team: mapTeam(input.team, input),
    checkedInAt: text(input.checkedInAt ?? input.checked_in_at) || null,
    createdAt: text(input.createdAt ?? input.created_at) || undefined,
    paidAt: text(input.paidAt ?? input.paid_at) || null,
    qrToken: text(input.qrToken ?? input.qr_token) || undefined,
    referralToken: text(input.referralToken ?? input.referral_token) || undefined,
    paidReferralsCount: numeric(input.paidReferralsCount ?? input.paid_referrals_count) ?? 0,
    isInactive: Boolean(input.deletedAt ?? input.deleted_at),
    inactiveReason: text(input.inactiveReason ?? input.inactive_reason) || null,
    ...(masked ? { masked: true } : {}),
  } as RegistrationSummary;
}

function mapDashboard(value: unknown): DashboardData {
  const input = record(value);
  const totals = record(input.totals);
  const readTotal = (name: string) => numeric(totals[name]) ?? 0;
  const labels: Record<string, string> = {
    page_view: 'Acessos na página', form_started: 'Inscrições iniciadas', registration_submitted: 'Inscrições realizadas', payment_paid: 'Pagamentos realizados', checkin_completed: 'Check-ins realizados',
  };
  const asPoints = (source: unknown, labelKey: string) => Array.isArray(source)
    ? source.map((item) => ({ label: text(record(item)[labelKey]), value: numeric(record(item).count) ?? 0 }))
    : [];
  const teamRows = Array.isArray(input.registrationsByTeam) ? input.registrationsByTeam.map((item) => {
    const row = record(item);
    return { name: text(row.name, 'Sem equipe'), color: text(row.color, '#4a8fe7'), count: numeric(row.count) ?? 0 };
  }) : [];
  const mapGroupDemographics = (source: unknown) => Array.isArray(source) ? source.map((item) => {
    const row = record(item);
    return {
      name: text(row.name, 'Sem grupo'), color: text(row.color, '#5d78a7'), count: numeric(row.count) ?? 0,
      age12To13: numeric(row.age_12_13) ?? 0, age14To15: numeric(row.age_14_15) ?? 0,
      age16To17: numeric(row.age_16_17) ?? 0, age18Plus: numeric(row.age_18_plus) ?? 0,
      femaleCount: numeric(row.female_count) ?? 0, maleCount: numeric(row.male_count) ?? 0,
      notInformedCount: numeric(row.not_informed_count) ?? 0,
      maleAge12To13: numeric(row.male_age_12_13) ?? 0, maleAge14To15: numeric(row.male_age_14_15) ?? 0,
      maleAge16To17: numeric(row.male_age_16_17) ?? 0, maleAge18Plus: numeric(row.male_age_18_plus) ?? 0,
      femaleAge12To13: numeric(row.female_age_12_13) ?? 0, femaleAge14To15: numeric(row.female_age_14_15) ?? 0,
      femaleAge16To17: numeric(row.female_age_16_17) ?? 0, femaleAge18Plus: numeric(row.female_age_18_plus) ?? 0,
    };
  }) : [];
  const groupDemographics = mapGroupDemographics(input.groupDemographics);
  const utms = Array.isArray(input.utms) ? input.utms.map((item) => {
    const row = record(item);
    return { name: text(row.source, 'direct'), count: numeric(row.count) ?? 0 };
  }) : [];
  const engagement = record(input.engagement);
  return {
    metrics: [
      { label: 'Arrecadação total', value: readTotal('total_revenue'), format: 'currency', detailLabel: 'Média por inscrição', detailValue: readTotal('average_revenue_per_registration'), detailFormat: 'currency' },
      { label: 'Arrecadação InfinitePay', value: readTotal('infinitepay_revenue'), format: 'currency' },
      { label: 'Arrecadação em dinheiro', value: readTotal('cash_revenue'), format: 'currency' },
      { label: 'Doações', value: readTotal('donations_total'), format: 'currency', detailLabel: `${Math.round(readTotal('donations_count')).toLocaleString('pt-BR')} lançamentos · média`, detailValue: readTotal('average_donation'), detailFormat: 'currency' },
      { label: 'Inscritos', value: readTotal('total_registrations'), format: 'number' },
      { label: 'Pagamentos aprovados', value: readTotal('total_paid'), format: 'number' },
      { label: 'Gratuitos / manuais', value: readTotal('total_manual_free'), format: 'number' },
      { label: 'Colaboradores', value: readTotal('total_collaborators'), format: 'number' },
      { label: 'Check-ins', value: readTotal('total_checkins'), format: 'number' },
      { label: 'Sem equipe', value: readTotal('without_team'), format: 'number' },
      { label: 'Taxa estimada', value: readTotal('estimated_fees'), format: 'currency' },
    ],
    funnel: Array.isArray(input.funnel) ? input.funnel.map((item) => {
      const row = record(item); const event = text(row.event_type);
      return { label: labels[event] ?? event, value: numeric(row.count) ?? 0 };
    }) : [],
    registrationsByDay: asPoints(input.registrationsByDay, 'day'),
    checkinsByHour: asPoints(input.checkinsByHour, 'hour'),
    registrationsByTeam: teamRows,
    groupDemographics,
    utmSources: utms,
    averageTimeOnPageSeconds: numeric(engagement.average_time) ?? 0,
    averageScrollPercent: numeric(engagement.average_scroll) ?? 0,
    timeP25: numeric(engagement.time_p25) ?? 0,
    timeP75: numeric(engagement.time_p75) ?? 0,
    scrollP25: numeric(engagement.scroll_p25) ?? 0,
    scrollP75: numeric(engagement.scroll_p75) ?? 0,
    timeSamples: Array.isArray(engagement.time_samples) ? engagement.time_samples.map((item) => numeric(item) ?? 0) : [],
    scrollSamples: Array.isArray(engagement.scroll_samples) ? engagement.scroll_samples.map((item) => numeric(item) ?? 0) : [],
  };
}

function mapDemographics(value: unknown): DemographicsData {
  const input = record(value);
  const mapGroups = (source: unknown) => Array.isArray(source) ? source.map((item) => {
    const row = record(item);
    return {
      name: text(row.name, 'Sem grupo'), color: text(row.color, '#5d78a7'), count: numeric(row.count) ?? 0,
      age12To13: numeric(row.age_12_13) ?? 0, age14To15: numeric(row.age_14_15) ?? 0, age16To17: numeric(row.age_16_17) ?? 0, age18Plus: numeric(row.age_18_plus) ?? 0,
      femaleCount: numeric(row.female_count) ?? 0, maleCount: numeric(row.male_count) ?? 0, notInformedCount: numeric(row.not_informed_count) ?? 0,
      maleAge12To13: numeric(row.male_age_12_13) ?? 0, maleAge14To15: numeric(row.male_age_14_15) ?? 0, maleAge16To17: numeric(row.male_age_16_17) ?? 0, maleAge18Plus: numeric(row.male_age_18_plus) ?? 0,
      femaleAge12To13: numeric(row.female_age_12_13) ?? 0, femaleAge14To15: numeric(row.female_age_14_15) ?? 0, femaleAge16To17: numeric(row.female_age_16_17) ?? 0, femaleAge18Plus: numeric(row.female_age_18_plus) ?? 0,
    };
  }) : [];
  return { scope: input.scope === 'checked_in' || input.scope === 'not_checked_in' ? input.scope : 'all', groups: mapGroups(input.groups) };
}

function session(value: unknown, persist = false): { user: SessionUser } {
  const input = record(value);
  const token = text(input.csrfToken);
  if (token) csrfToken = token;
  const fallbackToken = text(input.sessionToken);
  if (fallbackToken) {
    sessionToken = fallbackToken;
    try {
      if (persist) { window.localStorage.setItem(SESSION_TOKEN_KEY, fallbackToken); window.sessionStorage.removeItem(SESSION_TOKEN_KEY); }
      else { window.sessionStorage.setItem(SESSION_TOKEN_KEY, fallbackToken); window.localStorage.removeItem(SESSION_TOKEN_KEY); }
    } catch { /* Cookie remains the primary path. */ }
  }
  return { user: { actorType: text(input.actorType, 'admin') as SessionUser['actorType'], actorId: text(input.actorId) || undefined, expiresAt: text(input.expiresAt) || undefined } };
}

interface ApiErrorPayload {
  error?: string;
  message?: string;
  code?: string;
  details?: Record<string, string>;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly details?: Record<string, string>;

  constructor(status: number, message: string, payload?: ApiErrorPayload) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = payload?.code;
    this.details = payload?.details;
  }
}

function apiUrl(path: string): string {
  if (!path.startsWith('/')) throw new Error('API paths must begin with /');
  return `${API_BASE_URL}${path}`;
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);
  headers.set('Accept', 'application/json');
  if (options.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  if (csrfToken && ['POST', 'PATCH', 'PUT', 'DELETE'].includes(options.method ?? 'GET')) headers.set('X-CSRF-Token', csrfToken);
  if (sessionToken) headers.set('Authorization', `Bearer ${sessionToken}`);

  let response: Response;
  try {
    response = await fetch(apiUrl(path), {
      ...options,
      headers,
      credentials: 'include',
      cache: 'no-store',
    });
  } catch {
    throw new ApiError(0, 'Não foi possível conectar ao sistema. Verifique sua internet e tente novamente.');
  }

  const contentType = response.headers.get('content-type') ?? '';
  const payload: unknown = contentType.includes('application/json')
    ? await response.json().catch(() => null)
    : null;

  if (!response.ok) {
    const error = (payload ?? {}) as ApiErrorPayload;
    throw new ApiError(response.status, error.message ?? error.error ?? 'Algo deu errado. Tente novamente.', error);
  }

  // The Worker responds with direct JSON. Supporting a `data` envelope keeps the
  // frontend compatible with an API gateway without weakening its type boundary.
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return (payload as { data: T }).data;
  }
  return payload as T;
}

function post<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, {
    method: 'POST',
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

function patch<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
}

function mapRegistrationCreateResult(rawValue: unknown, data: RegistrationFormData): RegistrationCreateResult {
  const raw = record(rawValue);
  const nestedRegistration = record(raw.registration);
  const source = Object.keys(nestedRegistration).length ? nestedRegistration : raw;
  const checkoutUrl = text(raw.checkoutUrl ?? source.checkoutUrl) || undefined;
  return {
    registration: mapRegistration({
      ...source,
      registrationCode: source.registrationCode ?? raw.registrationCode,
      status: source.status ?? raw.status,
      paymentStatus: source.paymentStatus ?? raw.paymentStatus,
      qrToken: source.qrToken ?? source.qr_token ?? raw.qrToken ?? raw.qr_token,
      registrationType: source.registrationType ?? data.registrationType,
      participantName: source.participantName ?? data.participantName,
    }),
    checkoutUrl,
    paymentRequired: typeof raw.paymentRequired === 'boolean' ? raw.paymentRequired : !Boolean(raw.checkoutUnavailable),
    duplicate: Boolean(raw.duplicate),
    alreadyRegistered: Boolean(raw.alreadyRegistered),
  };
}

export const api = {
  track(eventType: string, context: TrackingContext, registrationCode?: string): Promise<ApiSuccess> {
    return post('/api/events/track', { eventType, registrationCode, ...context });
  },

  communityStats(): Promise<{ clicks: number; uniqueDevices: number }> {
    return request('/api/events/community-stats');
  },

  async createRegistration(data: RegistrationFormData): Promise<RegistrationCreateResult> {
    const path = data.registrationType === 'collaborator'
      ? '/api/collaborator-registrations'
      : '/api/registrations';
    const { privacyAccepted, registrationType: _registrationType, ...input } = data;
    return mapRegistrationCreateResult(await post<unknown>(path, { ...input, lgpdPrivacyAccepted: privacyAccepted }), data);
  },

  async quickCreateRegistration(data: RegistrationFormData): Promise<RegistrationCreateResult> {
    const { privacyAccepted, ...input } = data;
    return mapRegistrationCreateResult(await post<unknown>('/api/public-quick-registrations', {
      ...input,
      lgpdPrivacyAccepted: privacyAccepted,
    }), data);
  },

  async publicEventConfig(): Promise<PublicEventConfig> {
    const raw = record(await request('/api/events/config'));
    return { eventPriceCents: numeric(raw.eventPriceCents) ?? 3000, whatsappHelpNumber: text(raw.whatsappHelpNumber) || null, publicRegistrationDeadline: text(raw.publicRegistrationDeadline, '2026-07-19'), adminRegistrationDeadline: text(raw.adminRegistrationDeadline, '2026-07-31') };
  },

  async whatsappStatus(registrationCode: string) {
    return record(await post<unknown>('/api/registrations/whatsapp-status', { code: registrationCode }));
  },

  async resolveReferral(token: string): Promise<{ inviterName: string } | null> {
    const raw = record(await request(`/api/referrals/resolve${queryString({ token })}`));
    return raw.found === true && text(raw.inviterName) ? { inviterName: text(raw.inviterName) } : null;
  },

  async lookupRegistration(query: { registrationCode?: string; phone?: string }): Promise<PublicLookupResponse> {
    const raw = record(await post<unknown>('/api/registrations/lookup', query.registrationCode
      ? { code: query.registrationCode }
      : { phone: query.phone }));
    if (!raw.found) return {};
    if (raw.registration) return { registration: mapRegistration(raw.registration) as RegistrationLookup };
    if (Array.isArray(raw.registrations)) return { candidates: raw.registrations.map((item) => mapRegistration(item, true) as RegistrationLookup) };
    return {};
  },

  resendRegistration(query: { registrationCode?: string }): Promise<ApiSuccess> {
    return post('/api/registrations/resend', { code: query.registrationCode });
  },

  checkPayment(registrationCode: string, signal: { transactionNsu?: string | null; invoiceSlug?: string | null } = {}): Promise<{ paid: boolean; status: string }> {
    return post('/api/payments/infinitepay/check', { registrationCode, ...signal });
  },

  async createPaymentCheckout(registrationCode: string): Promise<{ checkoutUrl: string }> {
    const raw = record(await post<unknown>('/api/payments/infinitepay/create', { registrationCode }));
    const checkoutUrl = text(raw.checkoutUrl);
    if (!checkoutUrl) throw new ApiError(502, 'Não foi possível abrir o pagamento agora.');
    return { checkoutUrl };
  },

  async adminLogin(password: string, rememberMe = false): Promise<{ user: SessionUser }> {
    return session(await post('/api/admin/login', { password, rememberMe }), rememberMe);
  },

  async adminLogout(): Promise<ApiSuccess> {
    const result = await post<ApiSuccess>('/api/admin/logout');
    csrfToken = undefined;
    sessionToken = undefined;
    try { window.sessionStorage.removeItem(SESSION_TOKEN_KEY); } catch { /* ignore */ }
    try { window.localStorage.removeItem(SESSION_TOKEN_KEY); } catch { /* ignore */ }
    return result;
  },

  async adminMe(): Promise<{ user: SessionUser }> {
    return session(await request('/api/admin/me'));
  },

  async adminDashboard(): Promise<DashboardData> {
    return mapDashboard(await request('/api/admin/dashboard'));
  },

  async adminDemographics(scope: DemographicScope): Promise<DemographicsData> {
    return mapDemographics(await request(`/api/admin/demographics?scope=${encodeURIComponent(scope)}`));
  },

  async adminSettings(): Promise<AdminEventSettings> {
    const raw = record(await request('/api/admin/settings'));
    const wa = record(raw.whatsapp);
    return { eventPriceCents: numeric(raw.eventPriceCents) ?? 3000, whatsappHelpNumber: text(raw.whatsappHelpNumber) || null, publicRegistrationDeadline: text(raw.publicRegistrationDeadline, '2026-07-19'), adminRegistrationDeadline: text(raw.adminRegistrationDeadline, '2026-07-31'), whatsapp: { nomeEvento: text(wa.nomeEvento, '12 HORAS COM FORTEENS'), dataEvento: text(wa.dataEvento, '24/07/2026'), horarioEvento: text(wa.horarioEvento, '20h às 8h | Status: Aguardando pagamento'), utilityPriceUsd: numeric(wa.utilityPriceUsd) ?? .008, usdBrlRate: numeric(wa.usdBrlRate) ?? 5.5, rateUpdatedAt: text(wa.rateUpdatedAt) || null } };
  },

  async adminDeveloperMode(): Promise<DeveloperModeState> {
    const raw = record(await request('/api/admin/developer-mode'));
    return { enabled: raw.enabled === true, expiresAt: text(raw.expiresAt) || null };
  },

  async adminActivateDeveloperMode(password: string): Promise<DeveloperModeState> {
    const raw = record(await post<unknown>('/api/admin/developer-mode', { password }));
    return { enabled: raw.enabled === true, expiresAt: text(raw.expiresAt) || null };
  },

  async adminDeactivateDeveloperMode(): Promise<DeveloperModeState> {
    const raw = record(await request('/api/admin/developer-mode', { method: 'DELETE' }));
    return { enabled: raw.enabled === true, expiresAt: text(raw.expiresAt) || null };
  },

  async adminUpdateSettings(data: { eventPriceCents?: number; whatsappHelpNumber?: string | null; publicRegistrationDeadline?: string; adminRegistrationDeadline?: string; whatsapp?: import('../types').WhatsAppSettings & { restoreDefaults?: boolean } }): Promise<AdminEventSettings> {
    const raw = record(await patch<unknown>('/api/admin/settings', data));
    const wa = record(raw.whatsapp);
    return { eventPriceCents: numeric(raw.eventPriceCents) ?? 3000, whatsappHelpNumber: text(raw.whatsappHelpNumber) || null, publicRegistrationDeadline: text(raw.publicRegistrationDeadline, '2026-07-19'), adminRegistrationDeadline: text(raw.adminRegistrationDeadline, '2026-07-31'), whatsapp: { nomeEvento: text(wa.nomeEvento), dataEvento: text(wa.dataEvento), horarioEvento: text(wa.horarioEvento), utilityPriceUsd: numeric(wa.utilityPriceUsd) ?? .008, usdBrlRate: numeric(wa.usdBrlRate) ?? 5.5, rateUpdatedAt: text(wa.rateUpdatedAt) || null } };
  },

  adminWhatsAppDashboard(): Promise<import('../types').WhatsAppDashboard> { return request('/api/admin/whatsapp/dashboard'); },
  adminWhatsAppLogs(filters: Record<string, string | number | undefined>): Promise<{ items: Array<Record<string, unknown>>; total: number; page: number; pageSize: number }> { return request(`/api/admin/whatsapp/logs${queryString(filters)}`); },
  adminWhatsAppLogDetail(id: string): Promise<Record<string, unknown>> { return request(`/api/admin/whatsapp/logs/${encodeURIComponent(id)}`); },
  adminWhatsAppBatchPreview(operation: 'missing' | 'failed', limit: 1 | 10 | 20 | null): Promise<{ eligible: number; available: number; ignored: number; estimatedCostBrl: number }> { return request(`/api/admin/whatsapp/batch-preview${queryString({ operation, limit })}`); },
  adminStartWhatsAppBatch(operation: 'missing' | 'failed', limit: 1 | 10 | 20 | null): Promise<{ batchId: string; status: string }> { return post('/api/admin/whatsapp/batches', { operation, limit, confirmed: true }); },
  adminWhatsAppBatchStatus(id: string): Promise<Record<string, unknown>> { return request(`/api/admin/whatsapp/batches/${encodeURIComponent(id)}`); },
  adminQueueEventReminderTest(phone: string): Promise<{ queued: boolean; registrationCode: string; dispatchId: string }> { return post('/api/admin/whatsapp/event-reminder/test', { phone }); },
  adminQueueEventReminderBatch(): Promise<{ queued: number; skipped: number }> { return post('/api/admin/whatsapp/event-reminder/batch', { limit: 20, confirmed: true }); },
  adminProcessEventReminderQueue(): Promise<{ processing: number }> { return post('/api/admin/whatsapp/event-reminder/process', {}); },
  adminStartEventReminderCampaign(): Promise<{ campaignId: string; total: number }> { return post('/api/admin/whatsapp/event-reminder/all', {}); },
  adminEventReminderCampaignStatus(id: string): Promise<Record<string, unknown>> { return request(`/api/admin/whatsapp/event-reminder/all/${encodeURIComponent(id)}`); },
  adminEmailDashboard(): Promise<import('../types').EmailDashboard> { return request('/api/admin/email/dashboard'); },
  adminEmailLogs(filters: Record<string, string | number | undefined>): Promise<{ items: Array<Record<string, unknown>>; total: number; page: number; pageSize: number }> { return request(`/api/admin/email/logs${queryString(filters)}`); },
  adminEmailLogDetail(id: string): Promise<Record<string, unknown>> { return request(`/api/admin/email/logs/${encodeURIComponent(id)}`); },
  adminEmailBatchPreview(operation: 'missing' | 'failed', limit: 1 | 10 | 20 | null): Promise<{ eligible: number; available: number }> { return request(`/api/admin/email/batch-preview${queryString({ operation, limit })}`); },
  adminStartEmailBatch(operation: 'missing' | 'failed', limit: 1 | 10 | 20 | null): Promise<{ batchId: string; status: string }> { return post('/api/admin/email/batches', { operation, limit, confirmed: true }); },
  adminEmailBatchStatus(id: string): Promise<Record<string, unknown>> { return request(`/api/admin/email/batches/${encodeURIComponent(id)}`); },
  adminDatabaseUsage(): Promise<import('../types').DatabaseUsage> { return request('/api/admin/database-usage'); },

  async adminRegistrations(filters: Record<string, string | number | boolean | undefined | null>): Promise<PaginatedRegistrations> {
    const { perPage, teamId, ...rest } = filters;
    const raw = record(await request(`/api/admin/registrations${queryString({ ...rest, teamId: teamId === 'unassigned' ? undefined : teamId, withoutTeam: teamId === 'unassigned' ? true : undefined, pageSize: perPage })}`));
    return {
      items: Array.isArray(raw.items) ? raw.items.map((item) => mapRegistration(item)) : [],
      total: numeric(raw.total) ?? 0,
      page: numeric(raw.page) ?? 1,
      perPage: numeric(raw.pageSize) ?? numeric(perPage) ?? 20,
      summary: {
        total: numeric(record(raw.summary).total) ?? 0,
        averageAge: numeric(record(raw.summary).averageAge) ?? 0,
        dietaryCount: numeric(record(raw.summary).dietaryCount) ?? 0,
        forteensCount: numeric(record(raw.summary).forteensCount) ?? 0,
        cellCount: numeric(record(raw.summary).cellCount) ?? 0,
        confirmedCount: numeric(record(raw.summary).confirmedCount) ?? 0,
      }
    };
  },

  async adminManualFree(data: RegistrationFormData & { manualFreeReason?: string }): Promise<RegistrationSummary> {
    const { privacyAccepted, registrationType: _registrationType, manualFreeReason, ...input } = data;
    const raw = record(await post<unknown>('/api/admin/registrations/manual-free', {
      ...input,
      registrationType: data.registrationType,
      manualFreeReason: manualFreeReason || 'Cadastro manual autorizado pela administração',
      lgpdPrivacyAccepted: privacyAccepted,
    }));
    return mapRegistration(raw.registration ?? raw);
  },

  async adminUpdateRegistration(id: string, data: Partial<Omit<RegistrationFormData, 'foodRestriction' | 'attendsCell' | 'cellName' | 'responsibleName' | 'responsiblePhone'> & { status: string; paymentStatus: string; checkedIn: boolean }> & { foodRestriction?: string | null; sex?: 'female' | 'male' | null; attendsCell?: boolean | null; cellName?: string | null; responsibleName?: string | null; responsiblePhone?: string | null }): Promise<RegistrationSummary> {
    const raw = record(await patch<unknown>(`/api/admin/registrations/${encodeURIComponent(id)}`, data));
    return mapRegistration(raw.registration ?? raw);
  },

  async adminUpdatePaymentMode(id: string, mode: 'pending_payment' | 'manual_free' | 'cash'): Promise<RegistrationSummary> {
    const raw = record(await patch<unknown>(`/api/admin/registrations/${encodeURIComponent(id)}/payment-mode`, { mode }));
    return mapRegistration(raw.registration ?? raw);
  },

  async adminDeleteRegistration(id: string, reason?: string): Promise<ApiSuccess> {
    return request<ApiSuccess>(`/api/admin/registrations/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      body: JSON.stringify({ confirmed: true, reason }),
    });
  },

  async adminReactivateRegistration(id: string): Promise<RegistrationSummary> {
    const raw = record(await post<unknown>(`/api/admin/registrations/${encodeURIComponent(id)}/reactivate`));
    return mapRegistration(raw.registration ?? raw);
  },

  async adminDuplicateRegistrations(): Promise<Array<{ reason: string; registrations: RegistrationSummary[] }>> {
    const raw = record(await request('/api/admin/registrations/duplicates'));
    return Array.isArray(raw.groups) ? raw.groups.map((item) => {
      const group = record(item);
      return {
        reason: text(group.reason),
        registrations: Array.isArray(group.registrations) ? group.registrations.map((registration) => mapRegistration(registration)) : [],
      };
    }) : [];
  },

  adminDonations(page = 1, pageSize = 20): Promise<{ items: Array<Record<string, unknown>>; total: number; page: number; pageSize: number }> {
    return request(`/api/admin/donations${queryString({ page, pageSize })}`);
  },

  adminAssignTeam(id: string, teamId: string | null, reason?: string): Promise<ApiSuccess> {
    return patch(`/api/admin/registrations/${encodeURIComponent(id)}/team`, { teamId, reason });
  },

  async adminTeams(): Promise<Team[]> {
    const raw = record(await request('/api/admin/teams'));
    return Array.isArray(raw.teams) ? raw.teams.map((item) => mapTeam(item) ?? { id: text(record(item).id), name: text(record(item).name), color: text(record(item).color, '#4a8fe7'), isActive: true }) : [];
  },

  async adminCreateTeam(data: Pick<Team, 'name' | 'color'>): Promise<Team> {
    const raw = record(await post<unknown>('/api/admin/teams', data));
    return mapTeam(raw.team) ?? { id: '', name: data.name, color: data.color, isActive: true };
  },

  async adminUpdateTeam(id: string, data: Partial<Pick<Team, 'name' | 'color' | 'isActive'>>): Promise<Team> {
    const raw = record(await patch<unknown>(`/api/admin/teams/${encodeURIComponent(id)}`, data));
    return mapTeam(raw.team) ?? { id, name: text(data.name), color: text(data.color, '#4a8fe7'), isActive: data.isActive ?? true };
  },

  async adminExportCsv(filters: Record<string, string | number | boolean | undefined | null>): Promise<Blob> {
    let response: Response;
    try {
      response = await fetch(apiUrl(`/api/admin/export.csv${queryString(filters)}`), {
        credentials: 'include',
      });
    } catch {
      throw new ApiError(0, 'Não foi possível gerar a exportação agora.');
    }
    if (!response.ok) throw new ApiError(response.status, 'Não foi possível gerar a exportação agora.');
    return response.blob();
  },

  async checkinLogin(phone: string, password: string): Promise<{ user: SessionUser }> {
    return session(await post('/api/checkin/login', { phone, password }));
  },

  async checkinMe(): Promise<{ user: SessionUser }> {
    return session(await request('/api/checkin/me'));
  },

  async checkinLogout(): Promise<ApiSuccess> {
    const result = await post<ApiSuccess>('/api/checkin/logout');
    csrfToken = undefined;
    sessionToken = undefined;
    try { window.sessionStorage.removeItem(SESSION_TOKEN_KEY); } catch { /* ignore */ }
    return result;
  },

  async checkinScan(qrToken: string, deviceId?: string): Promise<CheckinResult> {
    const raw = record(await post<unknown>('/api/checkin/scan', { qrToken, deviceId }));
    return {
      outcome: text(raw.outcome, 'invalid_qr') as CheckinResult['outcome'],
      registration: raw.registration ? mapRegistration(raw.registration) : undefined,
      checkedInAt: text(raw.checkedInAt) || null,
      alreadyCheckedIn: raw.outcome === 'already_checked_in',
    };
  },

  async checkinByPhone(phone: string): Promise<CheckinCandidate[]> {
    const raw = record(await post<unknown>('/api/checkin/by-phone', { phone }));
    return Array.isArray(raw.registrations) ? raw.registrations.map((item) => mapRegistration(item) as CheckinCandidate) : [];
  },

  async checkinSearch(query: string): Promise<CheckinCandidate[]> {
    const raw = record(await post<unknown>('/api/checkin/search', { query }));
    return Array.isArray(raw.registrations) ? raw.registrations.map((item) => mapRegistration(item) as CheckinCandidate) : [];
  },

  async checkinHistory(deviceId: string, query = ''): Promise<CheckinCandidate[]> {
    const raw = record(await post<unknown>('/api/checkin/history', { deviceId, query }));
    return Array.isArray(raw.registrations) ? raw.registrations.map((item) => mapRegistration(item) as CheckinCandidate) : [];
  },

  async checkinConfirm(registrationId: string, deviceId?: string): Promise<CheckinResult> {
    const raw = record(await post<unknown>('/api/checkin/confirm', { registrationId, deviceId }));
    return {
      outcome: text(raw.outcome, 'not_found') as CheckinResult['outcome'],
      registration: raw.registration ? mapRegistration(raw.registration) : undefined,
      checkedInAt: text(raw.checkedInAt) || null,
      alreadyCheckedIn: raw.outcome === 'already_checked_in',
    };
  },
};
